import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { useLang } from "../i18n";

const NAV_ITEMS = [
  ["about", "/about"], ["services", "/services"], ["education", "/education-academics"],
  ["industries", "/industries"], ["insights", "/insights"], ["store", "/store"], ["contact", "/contact"],
];

export const Nav = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [pastHero, setPastHero] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [skyDark, setSkyDark] = useState(false);
  const location = useLocation();
  const menuButtonRef = useRef(null);
  const menuPanelRef = useRef(null);
  const servicesRef = useRef(null);
  const servicesTriggerRef = useRef(null);
  const isHome = location.pathname === "/";
  const compact = isHome && pastHero;
  const overHero = isHome && !scrolled && !open;

  const closeMenu = (restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) menuButtonRef.current?.focus();
  };

  const chooseCompactItem = () => {
    setPastHero(false);
    setOpen(false);
  };

  const toggleServices = (event) => {
    servicesTriggerRef.current = event.currentTarget;
    setServicesOpen((value) => !value);
  };

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 40);
      setPastHero(isHome && y > window.innerHeight * 0.8);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [isHome]);

  useEffect(() => {
    if (!isHome) {
      setSkyDark(false);
      return undefined;
    }
    const readSky = () => setSkyDark(document.documentElement.dataset.c1Sky === "dark");
    readSky();
    window.addEventListener("ruten-c1-sky", readSky);
    return () => window.removeEventListener("ruten-c1-sky", readSky);
  }, [isHome]);

  useEffect(() => {
    setOpen(false);
    setPastHero(false);
    setServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!servicesOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setServicesOpen(false);
        servicesTriggerRef.current?.focus();
      }
    };
    const onPointerDown = (event) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target)) setServicesOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [servicesOpen]);

  useEffect(() => {
    if (servicesOpen) servicesRef.current?.querySelector('[data-services-menu] a')?.focus();
  }, [servicesOpen]);

  useEffect(() => {
    if (!compact) setOpen(false);
  }, [compact]);

  useEffect(() => {
    if (!(compact && open)) return;
    menuPanelRef.current?.querySelector("a,button")?.focus();
  }, [compact, open]);

  useEffect(() => {
    if (!(compact && open)) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeMenu(true);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [compact, open]);

  const bar = overHero
    ? "bg-transparent border-transparent"
    : "bg-cream/90 backdrop-blur-xl border-sand";
  const ink = "text-charcoal";
  const navMarkSrc = overHero && skyDark ? "/images/logo-mark-light.png" : "/images/logo-mark-dark.png";

  const serviceSubLinks = (prefix, onNavigate) => (
    <>
      <Link
        to="/services"
        data-testid={`${prefix}-service-map`}
        onClick={onNavigate}
        className="block px-4 py-2.5 text-sm text-charcoal transition-colors duration-300 hover:bg-cream hover:text-coral-dark"
      >
        Service map
      </Link>
      <Link
        to="/services/index"
        data-testid={`${prefix}-service-index`}
        onClick={onNavigate}
        className="block px-4 py-2.5 text-sm text-charcoal transition-colors duration-300 hover:bg-cream hover:text-coral-dark"
      >
        A–Z index
      </Link>
    </>
  );

  const renderStackedNavItems = (prefix, onNavigate) => NAV_ITEMS.map(([key, to]) => {
    if (key !== "services") {
      return (
        <NavLink
          key={key}
          to={to}
          data-testid={`${prefix}-nav-${key}`}
          onClick={onNavigate}
          className={({ isActive }) =>
            `py-2.5 text-sm font-medium border-b border-sand/60 ${isActive ? "text-coral-dark" : "text-charcoal"}`
          }
        >
          {t(`nav.${key}`)}
        </NavLink>
      );
    }
    return (
      <div key={key} className="border-b border-sand/60">
        <div className="flex items-center justify-between gap-3">
          <NavLink
            to={to}
            data-testid={`${prefix}-nav-${key}`}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex-1 py-2.5 text-sm font-medium ${isActive ? "text-coral-dark" : "text-charcoal"}`
            }
          >
            {t(`nav.${key}`)}
          </NavLink>
          <button
            type="button"
            data-testid={`${prefix}-services-menu-button`}
            aria-label="Open services menu"
            aria-expanded={servicesOpen}
            aria-controls={`${prefix}-services-subnav`}
            onClick={toggleServices}
            className="p-2 text-charcoal/70 transition-colors duration-300 hover:text-charcoal"
          >
            <ChevronDown size={16} className={`transition-transform duration-300 ${servicesOpen ? "rotate-180" : ""}`} />
          </button>
        </div>
        {servicesOpen && (
          <div id={`${prefix}-services-subnav`} data-services-menu className="mb-2 ml-3 flex flex-col border-l border-sand/70">
            {serviceSubLinks(prefix, onNavigate)}
          </div>
        )}
      </div>
    );
  });

  const renderLanguageToggle = (compactMode = false) => (
    <div data-testid={compactMode ? "compact-lang-toggle" : "lang-toggle"} className="flex items-center rounded-full border p-0.5 border-sand">
      {["en", "jp"].map((l) => (
        <button
          key={l}
          type="button"
          data-testid={`${compactMode ? "compact-" : ""}lang-${l}`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1 text-[11px] font-mono tracking-wider rounded-full transition-colors duration-300 ${
            lang === l
              ? "bg-coral text-white"
              : "text-charcoal/60 hover:text-charcoal"
          }`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );

  return (
    <>
      <header
        data-testid="main-nav"
        aria-hidden={compact}
        className={`fixed top-0 inset-x-0 z-50 border-b transition-[color,background-color,border-color,transform,opacity] duration-500 motion-reduce:transition-none ${compact ? "pointer-events-none invisible -translate-y-full opacity-0" : "visible translate-y-0 opacity-100"} ${bar}`}
      >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 h-16 flex items-center justify-start gap-4">
        <Link to="/" data-testid="nav-logo" className={`mr-auto flex-shrink-0 flex items-center gap-3 ${ink}`}>
          <img src={navMarkSrc} alt="" aria-hidden="true" className="h-8 w-auto shrink-0" />
          <span className="flex flex-col leading-tight">
            <span className="font-serif text-[17px] whitespace-nowrap">Ruten Borealis</span>
            <span className="font-mono text-[9px] tracking-[0.18em] uppercase opacity-70 whitespace-nowrap hidden sm:inline">
              架け橋 Kakehashi · Japan–Canada
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex flex-shrink-0 items-center gap-6">
          {NAV_ITEMS.map(([key, to]) => {
            if (key === "services") {
              return (
                <div key={key} ref={servicesRef} className="relative flex items-center gap-1">
                  <NavLink
                    to={to}
                    data-testid={`nav-${key}`}
                    className={({ isActive }) =>
                      `text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                        isActive ? "text-coral-dark" : "text-charcoal/75 hover:text-charcoal"
                      }`
                    }
                  >
                    {t(`nav.${key}`)}
                  </NavLink>
                  <button
                    type="button"
                    data-testid="services-menu-button"
                    aria-label="Open services menu"
                    aria-expanded={servicesOpen}
                    aria-controls="services-dropdown"
                    onClick={toggleServices}
                    className="p-1 text-charcoal/70 transition-colors duration-300 hover:text-charcoal"
                  >
                    <ChevronDown size={14} className={`transition-transform duration-300 ${servicesOpen ? "rotate-180" : ""}`} />
                  </button>
                  {servicesOpen && (
                    <div
                      id="services-dropdown"
                      data-testid="services-dropdown"
                      data-services-menu
                      className="absolute left-0 top-full mt-3 min-w-44 rounded-xl border border-sand bg-cream-surface py-2 shadow-xl"
                    >
                      {serviceSubLinks("services", () => setServicesOpen(false))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <NavLink
                key={key}
                to={to}
                data-testid={`nav-${key}`}
                className={({ isActive }) =>
                  `text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                    isActive ? "text-coral-dark" : "text-charcoal/75 hover:text-charcoal"
                  }`
                }
              >
                {t(`nav.${key}`)}
              </NavLink>
            );
          })}
        </nav>

        <div className="flex flex-shrink-0 items-center gap-3">
          {renderLanguageToggle()}
          <Link
            to="/contact"
            data-testid="nav-book-consultation"
            className={`hidden md:inline-flex shrink-0 items-center gap-1.5 overflow-visible max-w-none whitespace-nowrap text-[13px] font-medium px-5 py-2 rounded-full transition-colors duration-300 ${
              overHero
                ? "bg-coral hover:bg-coral-dark text-white"
                : "bg-navy hover:bg-navy-surface text-cream-surface"
            }`}
          >
            {t("nav.cta")}
            <ArrowUpRight size={14} />
          </Link>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className={`lg:hidden p-1.5 ${ink}`}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && !compact && (
        <div className="lg:hidden bg-cream border-t border-sand" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-1">
            {renderStackedNavItems("mobile", () => {
              setOpen(false);
              setServicesOpen(false);
            })}
            <Link
              to="/contact"
              data-testid="mobile-book-consultation"
              className="mt-3 inline-flex items-center justify-center gap-1.5 bg-navy text-cream-surface text-sm font-medium px-4 py-3 rounded-full"
            >
              {t("nav.cta")}
              <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      )}
      </header>

      {compact && (
        <>
          <button
            ref={menuButtonRef}
            type="button"
            data-testid="homepage-menu-button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="homepage-menu-panel"
            onClick={() => (open ? closeMenu(true) : setOpen(true))}
            className="fixed top-4 right-4 z-[70] inline-flex items-center gap-2 rounded-full border border-cream/20 bg-navy/85 px-3.5 py-2 text-cream-surface shadow-lg backdrop-blur-md transition-colors duration-300 hover:bg-navy focus:outline-none focus:ring-2 focus:ring-cream/70 motion-reduce:transition-none"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
            <span className="text-[11px] font-mono tracking-[0.16em] uppercase">{open ? "Close" : "Menu"}</span>
          </button>

          {open && (
            <div
              ref={menuPanelRef}
              id="homepage-menu-panel"
              data-testid="homepage-menu-panel"
              role="dialog"
              aria-label="Site menu"
              className="fixed top-16 right-4 left-4 sm:left-auto sm:w-80 z-[70] rounded-2xl border border-sand bg-cream shadow-2xl overflow-hidden"
            >
              <div className="px-5 py-4 flex flex-col gap-1">
                <Link
                  to="/"
                  data-testid="compact-nav-logo"
                  onClick={() => chooseCompactItem()}
                  className="pb-3 mb-1 flex items-center gap-3 text-charcoal border-b border-sand/60"
                >
                  <img src="/images/logo-mark-dark.png" alt="" aria-hidden="true" className="h-8 w-auto shrink-0" />
                  <span className="flex flex-col leading-tight">
                    <span className="font-serif text-[17px] whitespace-nowrap">Ruten Borealis</span>
                    <span className="font-mono text-[9px] tracking-[0.18em] uppercase opacity-70 whitespace-nowrap">
                      架け橋 Kakehashi · Japan–Canada
                    </span>
                  </span>
                </Link>
                {renderStackedNavItems("compact", () => {
                  chooseCompactItem();
                  setServicesOpen(false);
                })}
                <div className="mt-3 flex items-center justify-between gap-3">
                  {renderLanguageToggle(true)}
                  <Link
                    to="/contact"
                    data-testid="compact-book-consultation"
                    onClick={() => chooseCompactItem()}
                    className="inline-flex items-center justify-center gap-1.5 bg-navy text-cream-surface text-sm font-medium px-4 py-3 rounded-full"
                  >
                    {t("nav.cta")}
                    <ArrowUpRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};
